import { useEffect, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  MapPin,
  Phone,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { db } from "../lib/data";
import { supabase } from "../lib/supabase";
import { StatusPill, PageHeader } from "../components/shared/Primitives";
export function TechnicianHome() {
  const { user } = useAuth(),
    [jobs, setJobs] = useState([]);
  useEffect(() => {
    db.technicians().then((ts) => {
      const t = ts.find((x) => x.user_id === user?.id);
      if (t) db.tickets({ technicianId: t.id }).then(setJobs);
    });
  }, [user]);
  return (
    <div className="tech-page">
      <div className="tech-hero">
        <div>
          <span>Field service</span>
          <h1>Today's jobs.</h1>
          <p>Manage visits, payments and customer completion verification.</p>
        </div>
        <Wrench size={32} />
      </div>
      <div className="tech-kpis">
        <div>
          <span>Assigned</span>
          <b>{jobs.length}</b>
        </div>
        <div>
          <span>Open</span>
          <b>
            {
              jobs.filter((x) => !["completed", "closed"].includes(x.status))
                .length
            }
          </b>
        </div>
        <div>
          <span>Completed</span>
          <b>
            {
              jobs.filter((x) => ["completed", "closed"].includes(x.status))
                .length
            }
          </b>
        </div>
      </div>
      <div className="tech-job-list">
        {jobs.slice(0, 6).map((j) => (
          <Job key={j.id} j={j} />
        ))}
      </div>
    </div>
  );
}
export function TechnicianJobs() {
  const { user } = useAuth(),
    [jobs, setJobs] = useState([]);
  useEffect(() => {
    db.technicians().then((ts) => {
      const t = ts.find((x) => x.user_id === user?.id);
      if (t) db.tickets({ technicianId: t.id }).then(setJobs);
    });
  }, [user]);
  return (
    <div className="tech-page">
      <PageHeader
        title="My jobs"
        description="Live assigned service tickets."
      />
      <div className="tech-job-list">
        {jobs.map((j) => (
          <Job key={j.id} j={j} />
        ))}
      </div>
    </div>
  );
}
function Job({ j }) {
  return (
    <Link className="tech-job-card" to={`/technician/jobs/${j.id}`}>
      <div className="tech-job-top">
        <span className="tech-ticket-id">{j.ticket_number}</span>
        <StatusPill value={j.status} />
      </div>
      <h3>{j.subject}</h3>
      <div className="tech-job-meta">
        <span>
          <MapPin size={14} />
          {j.profiles?.full_name || "Customer"}
        </span>
        <span>{j.type}</span>
      </div>
    </Link>
  );
}
export function TechnicianJobDetail() {
  const { id } = useParams(),
    nav = useNavigate(),
    [job, setJob] = useState(null),
    [stage, setStage] = useState(""),
    [diagnosis, setDiagnosis] = useState(""),
    [work, setWork] = useState(""),
    [charge, setCharge] = useState("0"),
    [paid, setPaid] = useState(false),
    [sent, setSent] = useState(false),
    [otp, setOtp] = useState(""),
    [error, setError] = useState("");
  useEffect(() => {
    supabase
      .from("service_tickets")
      .select(
        "*,profiles!service_tickets_customer_id_fkey(full_name,phone),products(name,display_name,sku)",
      )
      .eq("id", id)
      .single()
      .then((x) => {
        setJob(x.data);
        setStage(x.data?.status || "");
      });
  }, [id]);
  if (!job) return <div>Loading…</div>;
  const setStatus = async (s) => {
    const { error } = await supabase
      .from("service_tickets")
      .update({ status: s })
      .eq("id", id);
    if (error) setError(error.message);
    else setStage(s);
  };
  const payment = async () => {
    const { error } = await supabase.functions.invoke(
      "record-service-payment",
      { body: { ticketId: id, amount: Number(charge) } },
    );
    if (error) setError(error.message);
    else setPaid(true);
  };
  const sendOtp = async () => {
    const { error } = await supabase.functions.invoke("generate-service-otp", {
      body: { ticketId: id },
    });
    if (error) setError(error.message);
    else setSent(true);
  };
  const complete = async () => {
    let r = await supabase.functions.invoke("verify-service-otp", {
      body: { ticketId: id, otp },
    });
    if (r.error) {
      setError(r.error.message);
      return;
    }
    r = await supabase.functions.invoke("complete-service", {
      body: {
        ticketId: id,
        diagnosis,
        workPerformed: work,
        completionNotes: "Customer OTP verified.",
      },
    });
    if (r.error) setError(r.error.message);
    else nav("/technician/jobs");
  };
  return (
    <div className="tech-page">
      <Link to="/technician/jobs">← Back</Link>
      <div className="tech-detail-grid">
        <div>
          <div className="tech-detail-head">
            <div>
              <span className="tech-ticket-id">{job.ticket_number}</span>
              <h1>{job.subject}</h1>
              <StatusPill value={stage} />
            </div>
            <a className="tech-call" href={`tel:${job.profiles?.phone || ""}`}>
              <Phone />
              Call
            </a>
          </div>
          <section className="tech-card">
            <div className="tech-card-title">
              <Wrench />
              Service
            </div>
            <p>
              {job.profiles?.full_name} · {job.profiles?.phone}
            </p>
            <p>
              {job.products?.display_name || job.products?.name} ·{" "}
              {job.products?.sku}
            </p>
            <p>{job.description}</p>
          </section>
          <section className="tech-card">
            <div className="tech-card-title">Work record</div>
            <textarea
              rows={4}
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              placeholder="Diagnosis"
            />
            <textarea
              rows={4}
              value={work}
              onChange={(e) => setWork(e.target.value)}
              placeholder="Work performed"
            />
            <div className="tech-flow">
              {["assigned", "en_route", "arrived", "in_progress"].map((s) => (
                <button
                  key={s}
                  className="tech-step"
                  onClick={() => setStatus(s)}
                >
                  {s.replaceAll("_", " ")}
                </button>
              ))}
            </div>
          </section>
          <section className="tech-card">
            <div className="tech-card-title">
              <CircleDollarSign />
              Payment & OTP
            </div>
            <input
              value={charge}
              onChange={(e) => setCharge(e.target.value)}
              placeholder="Final service charge"
            />
            {!paid ? (
              <button className="outline-btn" onClick={payment}>
                <CircleDollarSign />
                Record payment
              </button>
            ) : (
              <div className="tech-paid">
                <CheckCircle2 />
                Payment recorded
              </div>
            )}
            {!sent ? (
              <button
                disabled={!paid}
                className="outline-btn"
                onClick={sendOtp}
              >
                <ShieldCheck />
                Send customer OTP
              </button>
            ) : (
              <>
                <p className="tech-help">
                  OTP sent through the configured notification provider.
                </p>
                <input
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="Customer OTP"
                  maxLength={6}
                />
                <button
                  disabled={otp.length !== 6}
                  className="outline-btn"
                  onClick={complete}
                >
                  <CheckCircle2 />
                  Verify & close
                </button>
              </>
            )}
            {error && <div className="form-error">{error}</div>}
          </section>
        </div>
        <aside>
          <div className="tech-card">
            <div className="tech-card-title">
              <CalendarDays />
              Appointment
            </div>
            <p>
              {job.scheduled_start
                ? new Date(job.scheduled_start).toLocaleString("en-IN")
                : "Not scheduled"}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
export function TechnicianSchedule() {
  const { user } = useAuth(),
    [jobs, setJobs] = useState([]);
  useEffect(() => {
    db.technicians().then((ts) => {
      const t = ts.find((x) => x.user_id === user?.id);
      if (t)
        db.tickets({ technicianId: t.id }).then((x) =>
          setJobs(x.filter((j) => j.scheduled_start)),
        );
    });
  }, [user]);
  return (
    <div className="tech-page">
      <PageHeader title="Schedule" description="Assigned visits." />
      <div className="tech-card">
        {jobs.map((j) => (
          <div className="tech-calendar-row" key={j.id}>
            <b>{new Date(j.scheduled_start).toLocaleString("en-IN")}</b>
            <span>
              <strong>{j.ticket_number}</strong>
              <small>{j.subject}</small>
            </span>
            <StatusPill value={j.status} />
          </div>
        ))}
      </div>
    </div>
  );
}
export function TechnicianProfile() {
  const { user } = useAuth();
  return (
    <div className="tech-page">
      <PageHeader title="Profile" description="Technician account." />
      <div className="tech-card">
        <h2>{user?.name}</h2>
        <p>{user?.email}</p>
        <p>{user?.phone}</p>
      </div>
    </div>
  );
}
